import { useEffect, useRef, useState } from "react";

const stats = [
  {
    label: "Projects Delivered",
    value: 25,
    suffix: "+",
  },
  {
    label: "Happy Clients",
    value: 18,
    suffix: "+",
  },
  {
    label: "Digital Solutions Built",
    value: 30,
    suffix: "+",
  },
];

export default function StatsSection() {
  const [counts, setCounts] = useState(stats.map(() => 0));
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;

    const duration = 1800;
    const steps = 60;
    const intervalTime = duration / steps;

    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;

      setCounts(
        stats.map((stat) => {
          const progress = currentStep / steps;

          const easedProgress =
            1 - Math.pow(1 - Math.min(progress, 1), 3);

          return Math.floor(stat.value * easedProgress);
        })
      );

      if (currentStep >= steps) {
        setCounts(stats.map((stat) => stat.value));
        clearInterval(interval);
      }
    }, intervalTime);

    return () => clearInterval(interval);
  }, [started]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-blue-600 py-20 md:py-24 text-white"
    >
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950"></div>

      <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-white/10 blur-[120px]"></div>

      <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] rounded-full bg-cyan-400/10 blur-[140px]"></div>

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
        }}
      ></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-md px-4 py-2 text-sm font-semibold text-blue-100 mb-5">
            <span className="w-2 h-2 rounded-full bg-cyan-300"></span>
            Our Impact
          </div>

          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            Building measurable impact through technology.
          </h2>

          <p className="text-blue-100 text-lg leading-relaxed">
            Every solution we build is focused on helping businesses,
            organizations, and communities work smarter and grow.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="group relative rounded-3xl border border-white/15 bg-white/10 backdrop-blur-xl p-8 md:p-10 text-center transition-all duration-500 hover:-translate-y-2 hover:bg-white/15 hover:border-white/30"
            >
              {/* Top glow */}
              <div className="absolute left-1/2 top-0 -translate-x-1/2 w-24 h-[2px] bg-gradient-to-r from-transparent via-cyan-300 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Number */}
              <div className="flex items-center justify-center">
                <span className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight">
                  {counts[i].toLocaleString()}
                </span>

                <span className="text-3xl md:text-4xl font-bold text-cyan-300 ml-1">
                  {stat.suffix}
                </span>
              </div>

              {/* Separator */}
              <div className="w-10 h-[2px] bg-blue-300/60 mx-auto my-5"></div>

              {/* Label */}
              <p className="text-blue-100 text-base md:text-lg font-medium">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom message */}
        <div className="flex justify-center mt-12">
          <p className="text-sm md:text-base text-blue-200 text-center">
            And we're just getting started.
          </p>
        </div>
      </div>
    </section>
  );
}